import {
  CircleGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  RingGeometry,
} from "three";
import type { Aircraft } from "../aircraft/Aircraft";
import type { Vec2 } from "../airport/AirportDefinition";
import { PATH_DASH } from "../config/gameplay";
import { LAYER, PALETTE, mixColor } from "../config/palette";
import type { LandingIntent } from "../simulation/RouteSystem";
import { ribbonGeometry } from "./geometry";

export interface DrawingLike {
  readonly aircraft: Aircraft;
  readonly points: readonly Vec2[];
}

/**
 * Draws every flight path as a flat ribbon.
 *
 * Meshes are pooled and hidden rather than destroyed, because the set of paths
 * changes every frame while the player is drawing.
 */
export class RouteView {
  readonly group = new Group();
  private readonly pool: Mesh[] = [];

  /**
   * The pen tip and the gate ring: one each, built once.
   *
   * They are the only feedback the player gets *before* letting go, so they
   * are drawn from the same intent the release will be judged against — the
   * ribbon colour and these two always agree.
   */
  private readonly penGeometry = new CircleGeometry(1, 14);
  private readonly gateGeometry = new RingGeometry(0.72, 1, 26);
  private readonly penTip: Mesh;
  private readonly gateRing: Mesh;

  constructor() {
    this.group.name = "flight-paths";

    this.penTip = new Mesh(
      this.penGeometry,
      new MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    );
    this.penTip.name = "route-pen-tip";
    this.penTip.renderOrder = 12;
    this.penTip.visible = false;

    this.gateRing = new Mesh(
      this.gateGeometry,
      new MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    );
    this.gateRing.name = "route-gate-ring";
    this.gateRing.renderOrder = 12;
    this.gateRing.visible = false;

    this.group.add(this.penTip, this.gateRing);
  }

  sync(
    aircraft: readonly Aircraft[],
    drawing: DrawingLike | null,
    highlightedId: number | null,
    intent: LandingIntent | null,
    time: number,
  ): void {
    let used = 0;
    const fastPulse = (Math.sin(time * 12) + 1) / 2;

    const emit = (
      points: readonly Vec2[],
      color: number,
      width: number,
      opacity: number,
      dashed = false,
      dashOffset = 0,
      // Annotated so the frozen defaults do not narrow these to their own
      // literals — departures pass `PATH_DASH.departureLength` here.
      dashLength: number = PATH_DASH.length,
      dashGap: number = PATH_DASH.gap,
    ): void => {
      const geometry = ribbonGeometry(
        points,
        width,
        dashed,
        dashOffset,
        dashLength,
        dashGap,
      );
      if (!geometry) {
        return;
      }

      let mesh = this.pool[used];
      if (mesh) {
        mesh.geometry.dispose();
        mesh.geometry = geometry;
      } else {
        mesh = new Mesh(
          geometry,
          new MeshBasicMaterial({
            side: DoubleSide,
            transparent: true,
            depthTest: false,
            depthWrite: false,
          }),
        );
        mesh.renderOrder = 9;
        this.pool.push(mesh);
        this.group.add(mesh);
      }

      const material = mesh.material as MeshBasicMaterial;
      material.color.setHex(color);
      material.opacity = opacity;
      mesh.position.z = LAYER.path;
      mesh.visible = true;
      used++;
    };

    for (const item of aircraft) {
      if (item.state === "departing" && item.path.length > 0) {
        emit(
          [item.pos, ...item.path],
          0xffffff,
          1.55,
          0.5,
          true,
          time * 4.8,
          PATH_DASH.departureLength,
          PATH_DASH.gap,
        );
        continue;
      }

      if (
        (item.state !== "flying" && item.state !== "landing") ||
        item.path.length === 0 ||
        (drawing && item === drawing.aircraft)
      ) {
        continue;
      }

      const highlighted = item.id === highlightedId;
      const color = highlighted
        ? mixColor(PALETTE.pathActive, item.spec.color, 0.46)
        : mixColor(PALETTE.path, item.spec.color, 0.34);
      emit(
        [item.pos, ...item.path],
        color,
        highlighted ? 1.35 : 0.9,
        highlighted ? 0.86 : 0.48,
      );
    }

    if (drawing && drawing.points.length >= 2) {
      let color = mixColor(
        PALETTE.pathActive,
        drawing.aircraft.spec.color,
        0.5,
      );
      let width = 1.25;
      let opacity = 0.88;

      if (intent?.status === "eligible") {
        // Flashes in the runway's colour: the landing will be accepted.
        color = mixColor(
          PALETTE.runway[intent.runway.color].marker,
          0xffffff,
          0.25 + fastPulse * 0.35,
        );
        width = 1.4 + fastPulse * 0.4;
        opacity = 0.92;
      } else if (intent) {
        // Wrong runway or wrong way round.
        color = mixColor(PALETTE.crash, PALETTE.rejectMix, fastPulse * 0.35);
        width = 1.4 + fastPulse * 0.5;
        opacity = 0.78 + (1 - fastPulse) * 0.2;
      }

      emit(drawing.points, color, width, opacity, true, time * 2.4);
      this.showPenTip(drawing.points[drawing.points.length - 1]!, color, time);
    } else {
      this.penTip.visible = false;
    }

    this.showGateRing(intent, time);

    for (let i = used; i < this.pool.length; i++) {
      this.pool[i]!.visible = false;
    }
  }

  /** A bright bead at the end of the ribbon, where the finger is. */
  private showPenTip(point: Vec2, color: number, time: number): void {
    const pulse = (Math.sin(time * 9) + 1) / 2;
    this.penTip.visible = true;
    this.penTip.position.set(point.x, point.y, LAYER.path + 0.02);
    this.penTip.scale.setScalar(0.85 + pulse * 0.32);
    const material = this.penTip.material as MeshBasicMaterial;
    material.color.setHex(mixColor(0xffffff, color, 0.35));
    material.opacity = 0.72 + pulse * 0.24;
  }

  /**
   * A ring on the gate the drop would use.
   *
   * Green-tinted and calm when the landing would be accepted, red and beating
   * when it would be refused — the whole point is that the answer arrives
   * before the player lets go, not after.
   */
  private showGateRing(intent: LandingIntent | null, time: number): void {
    if (!intent) {
      this.gateRing.visible = false;
      return;
    }

    const eligible = intent.status === "eligible";
    // A refusal on the wrong end is about the far threshold, not the gate.
    const anchor =
      intent.status === "direction"
        ? intent.runway.end
        : intent.runway.approach;
    const pulse = (Math.sin(time * (eligible ? 6 : 13)) + 1) / 2;

    this.gateRing.visible = true;
    this.gateRing.position.set(anchor.x, anchor.y, LAYER.path + 0.01);
    this.gateRing.scale.setScalar(
      (eligible ? 4.6 : 4.2) + pulse * (eligible ? 0.6 : 1.1),
    );

    const material = this.gateRing.material as MeshBasicMaterial;
    material.color.setHex(
      eligible
        ? mixColor(
            PALETTE.runway[intent.runway.color].marker,
            0xffffff,
            0.3 + pulse * 0.3,
          )
        : mixColor(PALETTE.crash, 0xffffff, pulse * 0.25),
    );
    material.opacity = eligible ? 0.6 + pulse * 0.25 : 0.72 + pulse * 0.2;
  }

  dispose(): void {
    for (const mesh of this.pool) {
      mesh.geometry.dispose();
      (mesh.material as MeshBasicMaterial).dispose();
    }
    this.pool.length = 0;
    (this.penTip.material as MeshBasicMaterial).dispose();
    (this.gateRing.material as MeshBasicMaterial).dispose();
    this.penGeometry.dispose();
    this.gateGeometry.dispose();
    this.group.clear();
  }
}
